/**
 * Features Page - Clinafy Institutional Website
 *
 * Detailed features presentation with alternating sections,
 * comparison table with all 5 plans, and conversion-focused CTAs.
 *
 * Updated to reflect REAL features from plans.ts config.
 */

import React from 'react';
import { useSEO, SEO_CONFIG } from '../../hooks/useSEO';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  alpha,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VideocamIcon from '@mui/icons-material/Videocam';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GroupsIcon from '@mui/icons-material/Groups';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import InsightsIcon from '@mui/icons-material/Insights';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarIcon from '@mui/icons-material/Star';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import QueueIcon from '@mui/icons-material/Queue';
import MicIcon from '@mui/icons-material/Mic';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ShareIcon from '@mui/icons-material/Share';

// Components
import Navbar from '../../components/landing/Navbar';
import Footer from '../../components/landing/Footer';
import ImageGallery from '../../components/landing/ImageGallery';
import { landingColors, landingGradients, landingShadows } from '../../theme/landingTheme';

// Plan config - SINGLE SOURCE OF TRUTH
import { PLAN_DEFINITIONS, formatPrice, PlanId } from '../../config/plans';

// Screenshots config
import { FEATURE_SCREENSHOTS } from '../../config/screenshots';

// ============================================================================
// Data - Based on REAL system features
// ============================================================================

const detailedFeatures = [
  {
    id: 'prontuario',
    icon: <PsychologyIcon sx={{ fontSize: 48 }} />,
    title: 'Prontuário Eletrônico Completo',
    subtitle: 'Foque no paciente. Nós cuidamos do registro.',
    description: 'Sistema de prontuário desenvolvido especificamente para profissionais de saúde mental, com campos estruturados para evolução clínica e histórico completo do paciente.',
    bullets: [
      'Registro completo de sessões e evolução',
      'Histórico do paciente sempre acessível',
      'Notas clínicas estruturadas',
      'Busca rápida em todas as anotações',
      'Anexos e documentos por paciente',
      'Conformidade com regulamentações profissionais',
    ],
    highlight: 'Essencial',
    highlightColor: landingColors.primary.main,
    imagePosition: 'right' as const,
    availableIn: 'Todos os planos',
  },
  {
    id: 'agenda',
    icon: <CalendarMonthIcon sx={{ fontSize: 48 }} />,
    title: 'Agenda Integrada',
    subtitle: 'Sua agenda trabalha enquanto você descansa.',
    description: 'Agenda profissional com visualização diária, semanal e mensal. Lembretes automáticos reduzem faltas significativamente.',
    bullets: [
      'Visualização diária, semanal e mensal',
      'Lembretes automáticos via Email',
      'Envio via WhatsApp com 1 clique (a partir do plano Clínica)',
      'Bloqueio de horários e férias',
      'Recorrência automática de sessões',
      'Status de confirmação do paciente',
    ],
    highlight: 'Automação',
    highlightColor: landingColors.primary.main,
    imagePosition: 'left' as const,
    availableIn: 'Todos os planos',
  },
  {
    id: 'teleconsulta',
    icon: <VideocamIcon sx={{ fontSize: 48 }} />,
    title: 'Teleconsulta Integrada',
    subtitle: 'Atenda de qualquer lugar. Sem complicação.',
    description: 'Vídeo consultas em alta qualidade diretamente no sistema via Jitsi Meet. Sem necessidade de instalar aplicativos extras.',
    bullets: [
      'Vídeo em alta qualidade',
      'Link automático enviado ao paciente',
      'Funciona em qualquer navegador',
      'Compartilhamento de tela',
      'Sem limite de duração',
      'Integrado ao prontuário',
    ],
    highlight: 'Vídeo HD',
    highlightColor: landingColors.status.info,
    imagePosition: 'right' as const,
    availableIn: 'A partir do plano Clínica',
  },
  {
    id: 'ia',
    icon: <AutoAwesomeIcon sx={{ fontSize: 48 }} />,
    title: 'Inteligência Artificial',
    subtitle: 'O futuro da saúde mental clínica. Hoje.',
    description: 'Tecnologia de ponta com transcrição automática de sessões e insights gerados por IA. Economize horas de trabalho administrativo e foque no que importa: seu paciente.',
    bullets: [
      'Transcrição automática de videochamadas',
      'Resumos inteligentes de cada sessão',
      'Detecção de padrões emocionais',
      'Sugestões de temas para próximas sessões',
      'Exportação de transcrições',
      'Privacidade total - dados criptografados',
    ],
    highlight: 'EXCLUSIVO',
    highlightColor: '#9333EA',
    imagePosition: 'left' as const,
    availableIn: 'A partir do plano Clínica Pro',
    isHighlighted: true,
  },
  {
    id: 'dashboard',
    icon: <AccountBalanceWalletIcon sx={{ fontSize: 48 }} />,
    title: 'Dashboard Financeiro',
    subtitle: 'Análises completas. Decisões inteligentes.',
    description: 'Acompanhe o faturamento, taxa de presença e desempenho da sua clínica em tempo real com gráficos e métricas detalhadas.',
    bullets: [
      'Faturamento total e por período',
      'Taxa de presença dos pacientes',
      'Receita perdida por ausências',
      'Gráfico de tendência de faturamento',
      'Top pacientes por faturamento',
      'Consultas recentes com status e valores',
    ],
    highlight: 'Análises',
    highlightColor: landingColors.accent.orange,
    imagePosition: 'left' as const,
    availableIn: 'A partir do plano Clínica',
  },
  {
    id: 'equipe',
    icon: <GroupsIcon sx={{ fontSize: 48 }} />,
    title: 'Multi-Profissionais',
    subtitle: 'Cresça sem limite. Gerencie com controle.',
    description: 'Ideal para clínicas com vários profissionais. Cada um com seu login, seus pacientes e permissões personalizadas.',
    bullets: [
      'Até 5 usuários no plano Clínica',
      'Até 15 usuários no plano Clínica Pro',
      'Usuários ilimitados no Enterprise',
      'Perfis de acesso personalizados',
      'Cada profissional vê apenas seus pacientes',
      'Gestão centralizada de configurações',
    ],
    highlight: 'Escalável',
    highlightColor: landingColors.status.warning,
    imagePosition: 'right' as const,
    availableIn: 'A partir do plano Clínica',
  },
  {
    id: 'documentos',
    icon: <AssignmentIcon sx={{ fontSize: 48 }} />,
    title: 'Documentos Clínicos Automatizados',
    subtitle: 'Gere documentos profissionais em segundos.',
    description: 'Sistema completo para criação e gestão de documentos clínicos. Templates personalizáveis para recibos, declarações de comparecimento e relatórios.',
    bullets: [
      'Templates de recibos personalizáveis',
      'Declarações de comparecimento automáticas',
      'Relatórios de evolução do paciente',
      'Geração automática com dados do sistema',
      'Download em PDF profissional',
      'Logo e dados da clínica incluídos',
    ],
    highlight: 'Automático',
    highlightColor: landingColors.accent.teal,
    imagePosition: 'right' as const,
    availableIn: 'A partir do plano Individual',
  },
  {
    id: 'avaliacoes',
    icon: <AssessmentIcon sx={{ fontSize: 48 }} />,
    title: 'Avaliações e Instrumentos Clínicos',
    subtitle: 'Aplique e acompanhe instrumentos padronizados.',
    description: 'Biblioteca de instrumentos de avaliação clínica com aplicação digital, pontuação automática e acompanhamento da evolução ao longo do tempo.',
    bullets: [
      'Biblioteca de instrumentos padronizados',
      'Aplicação digital para pacientes',
      'Pontuação automática',
      'Gráficos de evolução temporal',
      'Histórico completo de aplicações',
      'Comparação entre avaliações',
    ],
    highlight: 'Estruturado',
    highlightColor: landingColors.status.info,
    imagePosition: 'left' as const,
    availableIn: 'A partir do plano Individual',
  },
  {
    id: 'agendamento-portal',
    icon: <EventAvailableIcon sx={{ fontSize: 48 }} />,
    title: 'Agendamento Online e Portal do Paciente',
    subtitle: 'Seus pacientes no controle. Você no comando.',
    description: 'Página pública para pacientes solicitarem horários e portal seguro onde eles podem ver consultas, confirmar presença e acessar documentos.',
    bullets: [
      'Página pública de agendamento online',
      'Portal seguro do paciente via magic link',
      'Visualização de próximas consultas',
      'Confirmação de presença com 1 clique',
      'Acesso a recibos e declarações',
      'Cancelamento com antecedência',
    ],
    highlight: 'NOVO',
    highlightColor: '#9333EA',
    imagePosition: 'right' as const,
    availableIn: 'A partir do plano Individual',
  },
  {
    id: 'gestao-avancada',
    icon: <QueueIcon sx={{ fontSize: 48 }} />,
    title: 'Gestão Avançada de Agenda',
    subtitle: 'Otimize seu tempo e maximize sua ocupação.',
    description: 'Fila de espera automática com notificações, dashboard de progresso do paciente e voz para texto para documentação hands-free.',
    bullets: [
      'Fila de espera com notificações automáticas',
      'Notificação quando vagar horário',
      'Dashboard de progresso do paciente',
      'Métricas visuais de evolução',
      'Voz para texto em tempo real',
      'Documentação sem usar as mãos',
    ],
    highlight: 'Produtividade',
    highlightColor: landingColors.accent.orange,
    imagePosition: 'left' as const,
    availableIn: 'A partir do plano Clínica',
  },
  {
    id: 'seguranca',
    icon: <SecurityIcon sx={{ fontSize: 48 }} />,
    title: 'Segurança e Conformidade',
    subtitle: 'Seus dados. Nossa obsessão.',
    description: 'Segurança de nível bancário para proteger os dados dos seus pacientes. Conformidade total com LGPD.',
    bullets: [
      'Criptografia AES-256',
      'Servidores AWS no Brasil',
      'Backup automático frequente',
      'Conformidade LGPD',
      'Conexão segura (HTTPS)',
      'Logs de auditoria de acesso',
    ],
    highlight: 'LGPD',
    highlightColor: landingColors.neutral.gray600,
    imagePosition: 'right' as const,
    availableIn: 'Todos os planos',
    noScreenshot: true,
  },
];

const additionalFeatures = [
  {
    icon: <RecordVoiceOverIcon />,
    title: 'Transcrição Automática',
    description: 'Suas videochamadas transcritas automaticamente por IA. Nunca mais perca detalhes importantes.',
  },
  {
    icon: <SmartToyIcon />,
    title: 'Insights por IA',
    description: 'Análise de padrões emocionais, sugestões de temas e resumos inteligentes de cada sessão.',
  },
  {
    icon: <NotificationsActiveIcon />,
    title: 'Notificações Automáticas',
    description: 'Email automático e envio fácil via WhatsApp para lembretes e confirmações.',
  },
  {
    icon: <InsightsIcon />,
    title: 'Relatórios Avançados',
    description: 'Visualize métricas de atendimentos, financeiro, evolução de pacientes e muito mais.',
  },
  {
    icon: <MicIcon />,
    title: 'Voz para Texto',
    description: 'Transcreva notas de sessão por voz em tempo real. Documentação hands-free.',
  },
  {
    icon: <TrendingUpIcon />,
    title: 'Dashboard de Progresso',
    description: 'Acompanhe a evolução dos pacientes com métricas visuais e tendências ao longo do tempo.',
  },
  {
    icon: <ShareIcon />,
    title: 'Programa de Indicação',
    description: 'Indique colegas e ganhe benefícios. Sistema completo de referral com recompensas.',
  },
  {
    icon: <AccountCircleIcon />,
    title: 'Portal do Paciente',
    description: 'Seus pacientes acessam consultas, confirmam presença e baixam documentos com segurança.',
  },
];

// Comparison features - ACCURATE data from plans.ts
// trial, individual, small_clinic, clinic_pro, enterprise
interface ComparisonFeature {
  name: string;
  tooltip?: string;
  trial: boolean | string;
  individual: boolean | string;
  small_clinic: boolean | string;
  clinic_pro: boolean | string;
  enterprise: boolean | string;
}

const comparisonFeatures: ComparisonFeature[] = [
  // Core features
  {
    name: 'Prontuário Eletrônico',
    tooltip: 'Registro completo de sessões e evolução do paciente',
    trial: true, individual: true, small_clinic: true, clinic_pro: true, enterprise: true
  },
  {
    name: 'Agenda Integrada',
    tooltip: 'Calendário com agendamentos e status',
    trial: true, individual: true, small_clinic: true, clinic_pro: true, enterprise: true
  },
  {
    name: 'Relatórios Básicos',
    tooltip: 'Relatórios essenciais de atendimentos',
    trial: true, individual: true, small_clinic: true, clinic_pro: true, enterprise: true
  },

  // Patient Experience
  {
    name: 'Agendamento Online',
    tooltip: 'Página pública para pacientes solicitarem horários',
    trial: false, individual: true, small_clinic: true, clinic_pro: true, enterprise: true
  },
  {
    name: 'Portal do Paciente',
    tooltip: 'Pacientes acessam consultas, documentos e confirmam presença',
    trial: false, individual: true, small_clinic: true, clinic_pro: true, enterprise: true
  },

  // Documents & Assessments
  {
    name: 'Modelos de Documentos',
    tooltip: 'Templates para recibos, declarações e relatórios clínicos',
    trial: false, individual: true, small_clinic: true, clinic_pro: true, enterprise: true
  },
  {
    name: 'Geração de Documentos',
    tooltip: 'Geração automática de recibos e declarações',
    trial: false, individual: true, small_clinic: true, clinic_pro: true, enterprise: true
  },
  {
    name: 'Avaliações Clínicas',
    tooltip: 'Instrumentos padronizados com pontuação automática',
    trial: false, individual: true, small_clinic: true, clinic_pro: true, enterprise: true
  },

  // Advanced Scheduling
  {
    name: 'Fila de Espera',
    tooltip: 'Gestão de lista de espera com notificações automáticas',
    trial: false, individual: false, small_clinic: true, clinic_pro: true, enterprise: true
  },

  // Financial & Reports
  {
    name: 'Módulo Financeiro',
    tooltip: 'Controle de pagamentos, recibos e inadimplência',
    trial: false, individual: true, small_clinic: true, clinic_pro: true, enterprise: true
  },
  {
    name: 'Relatórios Avançados',
    tooltip: 'Relatórios detalhados com gráficos e exportação',
    trial: false, individual: false, small_clinic: true, clinic_pro: true, enterprise: true
  },
  {
    name: 'Dashboard Avançado',
    tooltip: 'Painel de métricas e indicadores da clínica',
    trial: false, individual: false, small_clinic: true, clinic_pro: true, enterprise: true
  },

  // Progress & Analytics
  {
    name: 'Dashboard de Progresso',
    tooltip: 'Acompanhamento visual da evolução do paciente',
    trial: false, individual: false, small_clinic: true, clinic_pro: true, enterprise: true
  },

  // Video & Communication
  {
    name: 'Teleconsulta (Vídeo)',
    tooltip: 'Videochamadas integradas via Jitsi Meet',
    trial: false, individual: false, small_clinic: true, clinic_pro: true, enterprise: true
  },
  {
    name: 'Lembretes por Email',
    tooltip: 'Notificações automáticas de consulta via email',
    trial: true, individual: true, small_clinic: true, clinic_pro: true, enterprise: true
  },
  {
    name: 'Envio via WhatsApp',
    tooltip: 'Lembretes automáticos via WhatsApp',
    trial: false, individual: false, small_clinic: true, clinic_pro: true, enterprise: true
  },

  // AI & Voice Features
  {
    name: 'Voz para Texto',
    tooltip: 'Transcrição de notas por voz em tempo real',
    trial: false, individual: false, small_clinic: false, clinic_pro: true, enterprise: true
  },
  {
    name: 'Transcrição IA',
    tooltip: 'Transcrição automática de sessões com inteligência artificial',
    trial: false, individual: false, small_clinic: false, clinic_pro: '50/mês', enterprise: 'Ilimitado'
  },
  {
    name: 'Insights IA',
    tooltip: 'Sugestões e análises baseadas em IA',
    trial: false, individual: false, small_clinic: false, clinic_pro: true, enterprise: true
  },

  // Marketing & Growth
  {
    name: 'Programa de Indicação',
    tooltip: 'Sistema de referral com recompensas',
    trial: false, individual: false, small_clinic: false, clinic_pro: false, enterprise: true
  },

  // Limits
  {
    name: 'Número de Usuários',
    tooltip: 'Profissionais que podem usar o sistema',
    trial: '1', individual: '1', small_clinic: '5', clinic_pro: '15', enterprise: 'Ilimitado'
  },
  {
    name: 'Limite de Pacientes',
    tooltip: 'Número máximo de pacientes ativos',
    trial: '10', individual: '50', small_clinic: 'Ilimitado', clinic_pro: 'Ilimitado', enterprise: 'Ilimitado'
  },

  // Premium Features
  {
    name: 'Branding Personalizado',
    tooltip: 'Logo e cores da sua clínica no sistema',
    trial: false, individual: false, small_clinic: false, clinic_pro: false, enterprise: true
  },
  {
    name: 'Acesso via API',
    tooltip: 'Integração com outros sistemas via API',
    trial: false, individual: false, small_clinic: false, clinic_pro: false, enterprise: true
  },
  {
    name: 'Suporte Prioritário',
    tooltip: 'Atendimento com prioridade',
    trial: false, individual: false, small_clinic: true, clinic_pro: true, enterprise: true
  },
  {
    name: 'Suporte Dedicado 24/7',
    tooltip: 'Gerente de conta dedicado',
    trial: false, individual: false, small_clinic: false, clinic_pro: false, enterprise: true
  },
];

// Plan display order for comparison table
const planOrder: PlanId[] = ['trial', 'individual', 'small_clinic', 'clinic_pro', 'enterprise'];

const planDisplayNames: Record<PlanId, string> = {
  trial: 'Teste',
  individual: 'Individual',
  small_clinic: 'Clínica',
  clinic_pro: 'Clínica Pro',
  enterprise: 'Enterprise',
};

// ============================================================================
// Component
// ============================================================================

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();

  // SEO - Dynamic meta tags
  useSEO(SEO_CONFIG.funcionalidades);

  const renderFeatureValue = (value: boolean | string) => {
    if (value === true) {
      return <CheckCircleIcon sx={{ color: landingColors.accent.teal, fontSize: 22 }} />;
    }
    if (value === false) {
      return <CancelIcon sx={{ color: landingColors.neutral.gray300, fontSize: 22 }} />;
    }
    return (
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: landingColors.primary.main }}>
        {value}
      </Typography>
    );
  };

  return (
    <Box sx={{ bgcolor: landingColors.background.default }}>
      <Navbar />

      <Box component="main" id="main-content">
      {/* ================================================================== */}
      {/* HERO SECTION */}
      {/* ================================================================== */}
      <Box
        sx={{
          background: landingGradients.hero,
          pt: { xs: 10, md: 12 },
          pb: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          {/* Badge */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Chip
              icon={<VerifiedUserIcon sx={{ fontSize: 18 }} />}
              label="Sistema Completo para Profissionais de Saúde Mental"
              sx={{
                bgcolor: alpha(landingColors.neutral.white, 0.15),
                color: landingColors.neutral.white,
                fontWeight: 500,
                fontSize: '0.9rem',
                py: 2.5,
                px: 1,
                '& .MuiChip-icon': { color: landingColors.accent.teal },
              }}
            />
          </Box>

          <Typography
            variant="h1"
            align="center"
            sx={{
              color: landingColors.neutral.white,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 2,
            }}
          >
            Funcionalidades feitas para profissionais de saúde mental
          </Typography>
          <Typography
            align="center"
            sx={{
              color: alpha(landingColors.neutral.white, 0.9),
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              maxWidth: 700,
              mx: 'auto',
              mb: 4,
            }}
          >
            Cada recurso foi desenvolvido pensando nas necessidades reais do dia a dia
            da prática clínica em saúde mental.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup?trial=true')}
              sx={{
                bgcolor: landingColors.neutral.white,
                color: landingColors.primary.main,
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': {
                  bgcolor: landingColors.neutral.gray100,
                },
              }}
            >
              Começar Teste Gratuito
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/planos')}
              sx={{
                borderColor: alpha(landingColors.neutral.white, 0.5),
                color: landingColors.neutral.white,
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: landingColors.neutral.white,
                  bgcolor: alpha(landingColors.neutral.white, 0.1),
                },
              }}
            >
              Ver Planos e Preços
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* DETAILED FEATURES SECTIONS */}
      {/* ================================================================== */}
      {detailedFeatures.map((feature: any, index) => {
        const isHighlighted = feature.isHighlighted;
        const noScreenshot = feature.noScreenshot;

        return (
          <Box
            key={index}
            sx={{
              py: { xs: 8, md: 10 },
              bgcolor: isHighlighted
                ? 'linear-gradient(135deg, #F3E8FF 0%, #E9D5FF 100%)'
                : index % 2 === 0
                  ? landingColors.background.default
                  : landingColors.background.alt,
              background: isHighlighted
                ? 'linear-gradient(135deg, #F3E8FF 0%, #E9D5FF 100%)'
                : undefined,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Destaque especial para seção de IA */}
            {isHighlighted && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  bgcolor: '#9333EA',
                  color: '#FFFFFF',
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  display: { xs: 'none', md: 'block' },
                }}
              >
                DIFERENCIAL COMPETITIVO
              </Box>
            )}

            <Container maxWidth="lg">
              <Grid
                container
                spacing={6}
                alignItems="center"
                direction={feature.imagePosition === 'left' ? 'row-reverse' : 'row'}
              >
                {/* Content */}
                <Grid size={{ xs: 12, md: noScreenshot ? 12 : 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Box
                      sx={{
                        color: isHighlighted ? '#9333EA' : landingColors.primary.main,
                        display: 'flex',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Chip
                      label={feature.highlight}
                      size="small"
                      sx={{
                        bgcolor: isHighlighted ? '#9333EA' : alpha(feature.highlightColor, 0.1),
                        color: isHighlighted ? '#FFFFFF' : feature.highlightColor,
                        fontWeight: 700,
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: isHighlighted ? '#581C87' : landingColors.neutral.gray900,
                      mb: 1,
                      fontSize: { xs: '1.75rem', md: isHighlighted ? '2.25rem' : '2rem' },
                    }}
                  >
                    {feature.title}
                  </Typography>

                  <Typography
                    sx={{
                      color: isHighlighted ? '#7C3AED' : landingColors.primary.main,
                      fontWeight: 500,
                      mb: 2,
                      fontSize: isHighlighted ? '1.2rem' : '1.1rem',
                    }}
                  >
                    {feature.subtitle}
                  </Typography>

                  <Typography
                    sx={{
                      color: isHighlighted ? '#6B21A8' : landingColors.neutral.gray600,
                      mb: 3,
                      lineHeight: 1.7,
                      fontSize: isHighlighted ? '1.05rem' : '1rem',
                    }}
                  >
                    {feature.description}
                  </Typography>

                  <Grid container spacing={1}>
                    {feature.bullets.map((bullet: string, bulletIndex: number) => (
                      <Grid key={bulletIndex} size={{ xs: 12, sm: noScreenshot ? 4 : 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <CheckCircleIcon
                            sx={{
                              fontSize: 18,
                              color: isHighlighted ? '#9333EA' : landingColors.accent.teal,
                              mt: 0.3,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: '0.9rem',
                              color: isHighlighted ? '#581C87' : landingColors.neutral.gray700,
                            }}
                          >
                            {bullet}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Availability indicator */}
                  <Box sx={{ mt: 3 }}>
                    <Chip
                      icon={<StarIcon sx={{ fontSize: 16 }} />}
                      label={feature.availableIn}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: isHighlighted ? '#9333EA' : landingColors.neutral.gray300,
                        color: isHighlighted ? '#9333EA' : landingColors.neutral.gray600,
                        fontSize: '0.8rem',
                      }}
                    />
                  </Box>
                </Grid>

                {/* Feature Screenshots - only if not noScreenshot */}
                {!noScreenshot && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    {feature.id && FEATURE_SCREENSHOTS[feature.id] ? (
                      <ImageGallery
                        images={FEATURE_SCREENSHOTS[feature.id].images}
                        title={feature.title}
                        accentColor={isHighlighted ? '#9333EA' : landingColors.primary.main}
                        aspectRatio={16 / 10}
                        showControls={true}
                        showDots={true}
                        showZoomHint={true}
                      />
                    ) : (
                      <Box
                        sx={{
                          bgcolor: isHighlighted
                            ? alpha('#9333EA', 0.08)
                            : alpha(landingColors.primary.main, 0.05),
                          borderRadius: 4,
                          p: 4,
                          minHeight: 350,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1px solid ${isHighlighted ? alpha('#9333EA', 0.2) : landingColors.neutral.gray200}`,
                        }}
                      >
                        <Typography sx={{ color: isHighlighted ? '#9333EA' : landingColors.neutral.gray400 }}>
                          [Screenshot: {feature.title}]
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                )}
              </Grid>
            </Container>
          </Box>
        );
      })}

      {/* ================================================================== */}
      {/* ADDITIONAL FEATURES */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: landingColors.background.alt }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 2,
            }}
          >
            E muito mais...
          </Typography>
          <Typography
            align="center"
            sx={{
              color: landingColors.neutral.gray600,
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Funcionalidades adicionais para otimizar sua prática clínica
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {additionalFeatures.map((feature, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    borderRadius: 3,
                    bgcolor: '#FFFFFF',
                    border: `1px solid ${landingColors.neutral.gray200}`,
                    boxShadow: 'none',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: landingShadows.card,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: alpha(landingColors.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      color: landingColors.primary.main,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: landingColors.neutral.gray900 }}>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ color: landingColors.neutral.gray600, fontSize: '0.9rem' }}>
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* COMPARISON TABLE - ALL 5 PLANS */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: landingColors.background.default }}>
        <Container maxWidth="xl">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 2,
            }}
          >
            Compare todos os planos
          </Typography>
          <Typography
            align="center"
            sx={{
              color: landingColors.neutral.gray600,
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Veja exatamente o que cada plano oferece. Dados baseados nas funcionalidades reais do sistema.
          </Typography>

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              boxShadow: landingShadows.card,
              overflow: 'auto',
              bgcolor: '#FFFFFF',
              // No mobile vira cards (bloco abaixo)
              display: { xs: 'none', md: 'block' },
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: landingColors.neutral.gray50 }}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      minWidth: 200,
                      color: landingColors.neutral.gray900,
                      borderBottom: `2px solid ${landingColors.neutral.gray200}`,
                    }}
                  >
                    Funcionalidade
                  </TableCell>
                  {planOrder.map((planId) => {
                    const plan = PLAN_DEFINITIONS[planId];
                    const isPopular = plan.isPopular;
                    return (
                      <TableCell
                        key={planId}
                        align="center"
                        sx={{
                          fontWeight: 600,
                          minWidth: 120,
                          color: landingColors.neutral.gray900,
                          bgcolor: isPopular ? alpha(landingColors.primary.main, 0.08) : 'inherit',
                          borderBottom: `2px solid ${landingColors.neutral.gray200}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          {isPopular && (
                            <Chip
                              label="Popular"
                              size="small"
                              sx={{
                                bgcolor: landingColors.primary.main,
                                color: '#FFFFFF',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: 20,
                              }}
                            />
                          )}
                          <Typography sx={{ fontWeight: 600, color: landingColors.neutral.gray900 }}>
                            {planDisplayNames[planId]}
                          </Typography>
                          <Typography variant="caption" sx={{ color: landingColors.neutral.gray500 }}>
                            {plan.price === 0 ? 'Grátis' : formatPrice(plan.price)}
                          </Typography>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonFeatures.map((feature, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:hover': { bgcolor: landingColors.neutral.gray50 },
                      '&:nth-of-type(odd)': { bgcolor: alpha(landingColors.neutral.gray100, 0.3) },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500, color: landingColors.neutral.gray800 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {feature.name}
                        {feature.tooltip && (
                          <Tooltip title={feature.tooltip} arrow placement="right">
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: landingColors.neutral.gray400, cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {planOrder.map((planId) => {
                      const isPopular = PLAN_DEFINITIONS[planId].isPopular;
                      return (
                        <TableCell
                          key={planId}
                          align="center"
                          sx={{
                            bgcolor: isPopular ? alpha(landingColors.primary.main, 0.04) : 'inherit',
                          }}
                        >
                          {renderFeatureValue(feature[planId])}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Versão mobile: um card por plano (tabela larga fica ilegível no celular) */}
          <Stack spacing={2.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {planOrder.map((planId) => {
              const plan = PLAN_DEFINITIONS[planId];
              return (
                <Card
                  key={planId}
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: plan.isPopular ? landingColors.primary.main : landingColors.neutral.gray200,
                    borderWidth: plan.isPopular ? 2 : 1,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: landingColors.neutral.gray900, fontSize: '1.1rem' }}>
                        {planDisplayNames[planId]}
                      </Typography>
                      <Typography sx={{ color: landingColors.neutral.gray600, fontSize: '0.9rem', fontWeight: 600 }}>
                        {plan.price === 0 ? 'Grátis' : `${formatPrice(plan.price)}/mês`}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    {comparisonFeatures.map((feature, i) => (
                      <Box
                        key={i}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, py: 0.6 }}
                      >
                        <Typography sx={{ color: landingColors.neutral.gray700, fontSize: '0.9rem' }}>
                          {feature.name}
                        </Typography>
                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                          {renderFeatureValue(feature[planId])}
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/planos')}
              sx={{
                bgcolor: landingColors.primary.main,
                color: '#FFFFFF',
                fontWeight: 600,
                '&:hover': { bgcolor: landingColors.primary.dark },
                px: 4,
                py: 1.5,
              }}
            >
              Ver Detalhes e Preços
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* FINAL CTA */}
      {/* ================================================================== */}
      <Box
        sx={{
          background: landingGradients.heroDark,
          py: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              color: landingColors.neutral.white,
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 2,
            }}
          >
            Pronto para experimentar?
          </Typography>
          <Typography
            sx={{
              color: alpha(landingColors.neutral.white, 0.8),
              fontSize: '1.1rem',
              mb: 4,
            }}
          >
            Teste todas as funcionalidades por {PLAN_DEFINITIONS.trial.limits.trialDays} dias. Sem compromisso. Sem cartão de crédito.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup?trial=true')}
              sx={{
                bgcolor: landingColors.neutral.white,
                color: landingColors.primary.main,
                fontWeight: 600,
                px: 5,
                py: 1.5,
                '&:hover': {
                  bgcolor: landingColors.neutral.gray100,
                },
              }}
            >
              Começar Teste Gratuito
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contato')}
              sx={{
                borderColor: alpha(landingColors.neutral.white, 0.5),
                color: landingColors.neutral.white,
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: landingColors.neutral.white,
                  bgcolor: alpha(landingColors.neutral.white, 0.1),
                },
              }}
            >
              Falar com Especialista
            </Button>
          </Box>
        </Container>
      </Box>

      </Box>

      <Footer />
    </Box>
  );
};

export default FeaturesPage;
